import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Icon, Message, Table } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageContent, PageHeader, PageSection, Subtitle } from 'common/styles/page';

import {
  AssessmentQuestion,
  AssessmentQuestionTypeEnum,
  AssessmentQuestionSet,
  useListPillarsQuery,
  useRetrieveQuestionSetQuery,
  useUpdateQuestionSetMutation,
} from '../api/pulseSurveyApi';
const GLOBAL_SCOPE = 3;
const NA_THEME_VALUE = '__na_theme__';
const ALL_FILTER_VALUE = '__all__';

const THEME_OPTIONS = [
  { id: 1, label: 'Engagement & Belonging' },
  { id: 2, label: 'Communication & Voice' },
  { id: 3, label: 'Growth & Development' },
  { id: 4, label: 'Fairness/Recognition & Rewards' },
  { id: 5, label: 'Leadership & Manager Support' },
  { id: 6, label: 'Collaboration & Relationships' },
  { id: 7, label: 'Workload & Wellbeing' },
  { id: 8, label: 'Trust & Psychological Safety' },
  { id: 9, label: 'Diversity & Representation' },
] as const;

const NA_PILLAR_VALUE = '__na__';

const questionTypeOptions = [
  { key: 1, text: 'Agreement scale', value: 1 },
  { key: 2, text: 'Feedback', value: 2 },
];

const makeNewQuestion = (sort_order: number): AssessmentQuestion => ({
  question: '',
  type: 1,
  sort_order,
  pillar: null,
  theme: null,
  config: {},
});

const PulseSurveyEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [errors, setErrors] = useState<string | null>(null);
  const [draggingQuestionIndex, setDraggingQuestionIndex] = useState<number | null>(null);
  const [dragOverQuestionIndex, setDragOverQuestionIndex] = useState<number | null>(null);
  const [pillarFilter, setPillarFilter] = useState<string | number>(ALL_FILTER_VALUE);
  const [themeFilter, setThemeFilter] = useState<string | number>(ALL_FILTER_VALUE);

  const [form, setForm] = useState<AssessmentQuestionSet>({
    type: 1,
    title: '',
    scope: GLOBAL_SCOPE,
    is_archived: false,
    team: null,
    organization: null,
    questions: [],
  });

  const { data: loaded, isLoading, error } = useRetrieveQuestionSetQuery(
    { id: Number(id) },
    { skip: !isEdit }
  );

  const [updateQuestionSet] = useUpdateQuestionSetMutation();
  const { data: pillarsData = [] } = useListPillarsQuery();

  const pillarOptions = useMemo(() => {
    const base = [{ key: NA_PILLAR_VALUE, text: '(N/A)', value: NA_PILLAR_VALUE }];
    const pillars = (pillarsData || [])
      .map((pillar) => {
        const numericId = Number(pillar.id);
        if (!Number.isFinite(numericId)) return null;
        return {
          key: `pillar-${numericId}`,
          text: pillar.name || `Pillar ${numericId}`,
          value: numericId,
        };
      })
      .filter((option): option is { key: string; text: string; value: number } => option !== null);

    return [...base, ...pillars];
  }, [pillarsData]);

  const themeOptions = useMemo(
    () => [
      { key: NA_THEME_VALUE, text: '(N/A)', value: NA_THEME_VALUE },
      ...THEME_OPTIONS.map((theme) => ({
        key: `theme-${theme.id}`,
        text: theme.label,
        value: theme.id,
      })),
    ],
    []
  );

  const pillarFilterOptions = useMemo(
    () => [{ key: ALL_FILTER_VALUE, text: 'All Pillars', value: ALL_FILTER_VALUE }, ...pillarOptions],
    [pillarOptions]
  );

  const themeFilterOptions = useMemo(
    () => [{ key: ALL_FILTER_VALUE, text: 'All Themes', value: ALL_FILTER_VALUE }, ...themeOptions],
    [themeOptions]
  );

  const filteredRows = useMemo(() => {
    const source = form.questions || [];
    return source
      .map((question, sourceIndex) => ({ question, sourceIndex }))
      .filter(({ question }) => {
        const pillarMatches =
          pillarFilter === ALL_FILTER_VALUE
            ? true
            : pillarFilter === NA_PILLAR_VALUE
              ? question.pillar == null
              : Number(question.pillar) === Number(pillarFilter);

        const themeMatches =
          themeFilter === ALL_FILTER_VALUE
            ? true
            : themeFilter === NA_THEME_VALUE
              ? question.theme == null
              : Number(question.theme) === Number(themeFilter);

        return pillarMatches && themeMatches;
      });
  }, [form.questions, pillarFilter, themeFilter]);

  useEffect(() => {
    if (!loaded) return;
    setForm({
      ...loaded,
      type: 1,
      scope: GLOBAL_SCOPE,
      organization: null,
      team: null,
      questions:
        loaded.questions && loaded.questions.length > 0
          ? [...loaded.questions]
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((question) => ({
                ...question,
                type: question.type ?? 1,
                theme: question.theme ?? null,
              }))
          : [],
    });
  }, [loaded]);

  const updateQuestion = (index: number, patch: Partial<AssessmentQuestion>) => {
    setForm((prev) => {
      const nextQuestions = [...(prev.questions || [])];
      nextQuestions[index] = {
        ...nextQuestions[index],
        ...patch,
      };
      return { ...prev, questions: nextQuestions };
    });
  };

  const addQuestion = () => {
    setForm((prev) => {
      const questions = [...(prev.questions || [])];
      questions.push(makeNewQuestion(questions.length + 1));
      return { ...prev, questions };
    });
  };

  const removeQuestion = (index: number) => {
    setForm((prev) => {
      const filtered = (prev.questions || []).filter((_, i) => i !== index);
      const resequenced = filtered.map((q, i) => ({ ...q, sort_order: i + 1 }));
      return { ...prev, questions: resequenced };
    });
  };

  const moveQuestion = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    setForm((prev) => {
      const questions = [...(prev.questions || [])];
      if (fromIndex < 0 || fromIndex >= questions.length || toIndex < 0 || toIndex >= questions.length) {
        return prev;
      }

      const [moved] = questions.splice(fromIndex, 1);
      questions.splice(toIndex, 0, moved);

      const resequenced = questions.map((q, i) => ({ ...q, sort_order: i + 1 }));
      return { ...prev, questions: resequenced };
    });
  };

  const handleQuestionDragStart = (index: number) => {
    setDraggingQuestionIndex(index);
    setDragOverQuestionIndex(index);
  };

  const handleQuestionDragOver = (index: number, event: React.DragEvent) => {
    event.preventDefault();
    if (dragOverQuestionIndex !== index) {
      setDragOverQuestionIndex(index);
    }
  };

  const handleQuestionDrop = (dropIndex: number, event: React.DragEvent) => {
    event.preventDefault();
    if (draggingQuestionIndex !== null) {
      moveQuestion(draggingQuestionIndex, dropIndex);
    }
    setDraggingQuestionIndex(null);
    setDragOverQuestionIndex(null);
  };

  const handleQuestionDragEnd = () => {
    setDraggingQuestionIndex(null);
    setDragOverQuestionIndex(null);
  };

  const normalizePayload = (): AssessmentQuestionSet => {
    const questions = (form.questions || [])
      .filter((q) => (q.question || '').trim().length > 0)
      .map((q, i) => ({
        question: q.question.trim(),
        type: q.type ?? 1,
        sort_order: i + 1,
        pillar: q.pillar ?? null,
        theme: q.theme ?? null,
        config: q.config || {},
      }));

    return {
      type: 1,
      title: form.title.trim(),
      scope: GLOBAL_SCOPE,
      is_archived: Boolean(form.is_archived),
      team: null,
      organization: null,
      questions,
    };
  };

  const handleSubmit = async () => {
    if (!form.title?.trim()) {
      setErrors('Title is required.');
      return;
    }

    setErrors(null);
    const payload = normalizePayload();

    try {
      await updateQuestionSet({ id: Number(id), payload }).unwrap();
      navigate('/settings/admin/pulse-surveys');
    } catch {
      setErrors('Failed to save question set.');
    }
  };

  if (isEdit && isLoading) {
    return <PageContent><PageSection><p>Loading question set...</p></PageSection></PageContent>;
  }

  if (isEdit && error) {
    return (
      <PageContent>
        <PageHeader title="Edit Question Set" />
        <PageSection>
          <Message negative content="Failed to load question set." />
        </PageSection>
      </PageContent>
    );
  }

  if (isEdit && loaded?.type === 2) {
    return (
      <PageContent>
        <PageHeader title="Edit Question Set" />
        <PageSection>
          <Message
            warning
            content="Assessment question sets are not editable from this Question Set UI."
          />
          <Button onClick={() => navigate('/settings/admin/pulse-surveys')}>Back to Question Sets</Button>
        </PageSection>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <PageHeader
        title="Edit Question Set"
        controls={
          <>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            <Button primary onClick={handleSubmit}>Save</Button>
          </>
        }
        divider
      />

      <PageSection>
        <Form>
          <Form.Input
            label="Title"
            value={form.title || ''}
            onChange={(_, { value }) => setForm((prev) => ({ ...prev, title: value }))}
            required
          />

          {errors && <Message negative content={errors} />}
        </Form>

        <PageSection padtop>
          <Subtitle>Questions</Subtitle>
        </PageSection>

        <PageSection display="flex" justifyContent="space-between" alignItems="center">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Form.Select
              compact
              selection
              options={pillarFilterOptions}
              value={pillarFilter}
              onChange={(_, { value }) => setPillarFilter(value as string | number)}
              placeholder="Filter by pillar"
              style={{ minWidth: 190 }}
            />
            <Form.Select
              compact
              selection
              options={themeFilterOptions}
              value={themeFilter}
              onChange={(_, { value }) => setThemeFilter(value as string | number)}
              placeholder="Filter by theme"
              style={{ minWidth: 210 }}
            />
          </div>
          <Button primary icon labelPosition="left" onClick={addQuestion}>
            <Icon name="plus" />
            Add Question
          </Button>
        </PageSection>

        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1}>Order</Table.HeaderCell>
              <Table.HeaderCell width={5}>Question</Table.HeaderCell>
              <Table.HeaderCell width={2}>Type</Table.HeaderCell>
              <Table.HeaderCell width={3}>Theme</Table.HeaderCell>
              <Table.HeaderCell width={3}>Pillar</Table.HeaderCell>
              <Table.HeaderCell width={1}>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredRows.map(({ question: q, sourceIndex }) => (
              <Table.Row
                key={`${q.id || 'new'}-${sourceIndex}`}
                onDragOver={(event: React.DragEvent) => handleQuestionDragOver(sourceIndex, event)}
                onDrop={(event: React.DragEvent) => handleQuestionDrop(sourceIndex, event)}
                style={
                  dragOverQuestionIndex === sourceIndex && draggingQuestionIndex !== sourceIndex
                    ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    : undefined
                }
              >
                <Table.Cell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      draggable
                      onDragStart={() => handleQuestionDragStart(sourceIndex)}
                      onDragEnd={handleQuestionDragEnd}
                      style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center' }}
                      title="Drag to reorder"
                    >
                      <Icon name="bars" />
                    </span>
                    <span>{sourceIndex + 1}</span>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <Form.Input
                    fluid
                    value={q.question}
                    onChange={(_, { value }) => updateQuestion(sourceIndex, { question: String(value || '') })}
                    placeholder="Enter question text"
                  />
                </Table.Cell>
                <Table.Cell>
                  <Form.Select
                    selection
                    options={questionTypeOptions}
                    value={q.type ?? 1}
                    onChange={(_, { value }) =>
                      updateQuestion(sourceIndex, { type: Number(value) as AssessmentQuestionTypeEnum })
                    }
                  />
                </Table.Cell>
                <Table.Cell>
                  <Form.Select
                    selection
                    options={themeOptions}
                    value={q.theme ?? NA_THEME_VALUE}
                    onChange={(_, { value }) => {
                      if (value === NA_THEME_VALUE) {
                        updateQuestion(sourceIndex, { theme: null });
                        return;
                      }
                      const parsed = Number(value);
                      updateQuestion(sourceIndex, { theme: Number.isFinite(parsed) ? parsed : null });
                    }}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Form.Select
                    selection
                    options={pillarOptions}
                    value={q.pillar ?? NA_PILLAR_VALUE}
                    onChange={(_, { value }) => {
                      if (value === NA_PILLAR_VALUE) {
                        updateQuestion(sourceIndex, { pillar: null });
                        return;
                      }
                      const parsed = Number(value);
                      updateQuestion(sourceIndex, { pillar: Number.isFinite(parsed) ? parsed : null });
                    }}
                  />
                </Table.Cell>
                <Table.Cell textAlign="center">
                  <Button icon="trash" size="mini" onClick={() => removeQuestion(sourceIndex)} />
                </Table.Cell>
              </Table.Row>
            ))}
            {filteredRows.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="6" textAlign="center">
                  No questions match the selected filters.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </PageSection>
    </PageContent>
  );
};

export default PulseSurveyEdit;
